import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import { Breadcrumb } from '@app/shared/components/breadcrumbs/breadcrumbs.component.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { CredentialService } from '@app/credential/services/credential.service';
import { EvaluationService } from '@app/tags/services/evaluation.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, ObservableInput, of, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { TrackingEventArgs } from '@app/shared/services/tracking.model';
import { TrackerService } from '@app/shared/services/tracker.service';
import { CheckpointService } from '@app/tags/services/checkpoint.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { Profile } from '@app/profile/profile.model';
import { TabRouterLessNavigationItem } from '@app/navigation/navigation.model';
import {
  ChartSignals,
  ChartTagSignals,
  SignalCount,
  TagSignalCount,
} from '@app/profile/profile-api.model';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { ProfileService } from '@app/profile/services/profile.service';
import { ProfileSkillSignalServiceV2 } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signal-page.service';

@Component({
  selector: 'dgx-profile-skill-signal-page',
  templateUrl: './profile-skill-signal-page.component.html',
  styleUrls: ['./profile-skill-signal-page.component.scss'],
})
export class ProfileSkillSignalPage
  extends SubscriberBaseDirective
  implements OnDestroy, OnInit
{
  public readonly CLASS_NAME = 'ProfileSkillSignalPage';
  @Input() public owner: Profile;

  public i18n = this.translateService.instant([
    'dgProfileOverview_TooltipSignalsPlural',
    'dgProfileOverview_TooltipSignalsSingle',
    'Core_ExperiencesDisplayName',
    'Core_Achievements',
    'Core_Learnings',
    'Core_All',
    'Core_Activity',
    'Core_Articles',
    'Core_Books',
    'Core_Ratings',
    'Core_Videos',
    'Core_Courses',
    'Core_Podcasts',
  ]);

  public isLoading: boolean;
  public tagId: number;
  public tag: TagsApi.TagDetails;
  public breadcrumbs: Breadcrumb[] = [];
  public profile: Profile;
  public authUser: AuthUser;
  public signalType: string = 'All';

  public availableRatings: TagsApi.RatingType[];
  public ratingDetails: TagsApi.UserTagRatingDetails[];
  public isTagEvaluable: boolean;
  public signalCount: TagSignalCount;
  public isMobile: boolean;
  public signalBreakdown: SignalCount[];
  public signalBreakdownLabel: string;
  public isLoggedIn: boolean;
  public chartSignals: ChartSignals;
  public allCheckpoints = [];
  public tabList: TabRouterLessNavigationItem[] = [
    {
      name: this.i18n.Core_All,
      nameId: 'All',
    },
    {
      name: this.i18n.Core_Ratings,
      nameId: 'Ratings',
    },
    {
      name: this.i18n.Core_ExperiencesDisplayName,
      nameId: 'Experiences',
    },
    {
      name: this.i18n.Core_Achievements,
      nameId: 'Achievements',
    },
    {
      name: this.i18n.Core_Learnings,
      nameId: 'Learnings',
    },
  ];

  public loadingStates = {
    achievements: true,
    page: true,
    learnings: true,
    experiences: true,
  };
  private ownerKey: number;
  private events: Subscription[] = [];

  public get ownerIsViewing() {
    return this.profileService.ownerIsViewing;
  }

  public get chartSignalsSignalCount(): SignalCount[] {
    // these are NOT really compatible, only thing shared is "counts", maybe that's all that's needed, but
    // I think this is a candidate for refactor (TODO:)
    return this.chartSignals?.signalCounts as unknown as SignalCount[];
  }

  constructor(
    private authService: AuthService,
    private tracker: TrackerService,
    private translateService: TranslateService,
    private tagsService: TagsService,
    private evalService: EvaluationService,
    private route: ActivatedRoute,
    private checkpointService: CheckpointService,
    private inputsService: InputsService,
    private windowLayoutService: WindowLayoutService,
    private profileService: ProfileService,
    private profileSkillSignalsService: ProfileSkillSignalServiceV2,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.events.push(
      this.inputsService.inputModify.subscribe((...args) => {
        this.updateSignalCountPopover();
      })
    );
  }

  public ngOnInit(): void {
    this.isMobile = this.windowLayoutService.isMobile;
    this.authUser = this.authService?.authUser;
    this.ownerKey = this.owner.id;
    this.isLoggedIn = !!this.authUser;
    this.updateLoadingState({ type: 'page', status: true });
    this.tagId = Number(this.route.snapshot.queryParams.tagId);

    forkJoin({
      skillsAndRatingsCount: this.profileSkillSignalsService.getUserSkills(
        this.ownerKey
      ),
      signalSummaries: this.profileSkillSignalsService.getSignalCounts(
        this.ownerKey
      ),
      signalBreakdown: this.profileSkillSignalsService.getSignalBreakdown(
        this.ownerKey,
        this.tagId.toString()
      ),
      signalSkillPage: this.profileSkillSignalsService.getSignalSkillPage(
        this.ownerKey,
        this.tagId.toString()
      ),
    })
      .pipe(
        take(1),
        switchMap(
          ({
            skillsAndRatingsCount,
            signalSummaries,
            signalBreakdown,
            signalSkillPage,
          }): ObservableInput<{
            availableRatings: TagsApi.RatingType[];
            ratingDetails: TagsApi.UserTagRatingDetails[];
            isTagEvaluable: boolean;
            allCheckpoints: TagsApi.Checkpoint[];
          }> => {
            this.signalBreakdown = signalBreakdown[0]?.signals;
            // @TODO: this is not ideal, I can't find an EP that will return a tag for a tagId and userId
            // which we need as this contains the ratings for that skill from that user
            const { skills } = skillsAndRatingsCount;
            this.tag = skills.other.find((s) => s.tagId === this.tagId);
            if (!this.tag) {
              this.tag = skills.focus.find((s) => s.tagId === this.tagId);
            }
            this.chartSignals = signalSkillPage;

            this.trackEvent('Skill Signal Viewed', {
              SkillId: this.tagId,
              SkillName: this.tag.name,
              ProfileOwnerId: this.ownerKey,
            });
            this.loadBreadcrumbs();

            // Setup the signal breakdown popover
            this.updateSignalCount(signalSummaries);

            let ratingCalls = {
              availableRatings: of([]),
              ratingDetails: of([]),
              isTagEvaluable: of(false),
              allCheckpoints: of([]),
            };
            if (!!this.authUser) {
              ratingCalls = {
                availableRatings: this.tagsService.getAvailableRatingTypes(
                  this.tagId
                ),
                ratingDetails: this.tagsService.getTagRatingDetails(
                  this.ownerKey,
                  this.tag.tagId
                ),
                isTagEvaluable: this.evalService.getIsEvaluableByTagId(
                  this.tag.tagId
                ),
                allCheckpoints: this.checkpointService.getAllCheckpoints(
                  this.tag?.tagId,
                  this.ownerKey
                ),
              };
            }
            return forkJoin({
              ...ratingCalls,
            });
          }
        )
      )
      .subscribe(
        ({
          availableRatings,
          ratingDetails,
          isTagEvaluable,
          allCheckpoints,
        }) => {
          this.availableRatings = availableRatings;
          this.ratingDetails = ratingDetails;
          this.isTagEvaluable = isTagEvaluable;
          this.allCheckpoints = allCheckpoints;
          this.updateLoadingState({ type: 'page', status: false });
          this.cdr.detectChanges();
        }
      );

    // Refresh view model when rating is updated
    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.getRatings();
      });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.events.forEach((sub) => sub.unsubscribe());
  }

  public updateLoadingState({
    type,
    status,
  }: {
    type: string;
    status: boolean;
  }): void {
    // Collect loading states from sub-components and this page, isLoading is changed to true when at least one of the
    // states is true and changed to false when all states have changed to false
    this.loadingStates[type] = status;
    this.isLoading = !!Object.values(this.loadingStates).find((v) => !!v);
  }

  public getRatings(): void {
    this.tagsService
      .getTagRatingDetails(this.ownerKey, this.tag.tagId)
      .subscribe((ratingDetails) => {
        this.ratingDetails = ratingDetails;
      });
    this.updateSignalCountPopover();
  }

  public switchTab(signalType: string = 'All'): void {
    // @TODO: probably need to add tracking here
    this.signalType = signalType;
  }

  public updateSignalCountPopover(): void {
    forkJoin({
      signalSummaries: this.profileSkillSignalsService.getSignalCounts(
        this.ownerKey
      ),
      signalBreakdown: this.profileSkillSignalsService.getSignalBreakdown(
        this.ownerKey,
        this.tagId.toString()
      ),
      signalSkillPage: this.profileSkillSignalsService.getSignalSkillPage(
        this.ownerKey,
        this.tagId.toString()
      ),
    })
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ signalSummaries, signalBreakdown, signalSkillPage }) => {
        this.signalBreakdown = signalBreakdown[0]?.signals;
        this.updateSignalCount(signalSummaries);
        this.chartSignals = signalSkillPage;
      });
  }

  public updateSignalCount(signalSummaries: ChartTagSignals): void {
    this.signalCount = signalSummaries.signalCounts.find(
      (count) => count.tagId === this.tag.tagId
    ) || { count: 0, tagId: undefined };
  }

  /**
   * Configure breadcrumbs for dgx-breadcrumbs component
   */
  private loadBreadcrumbs(): void {
    let breadcrumb: Breadcrumb;

    this.breadcrumbs = [];
    breadcrumb = { label: this.owner.name, routerLink: '../overview' };
    this.breadcrumbs.push(breadcrumb);

    if (this.tag?.name) {
      this.breadcrumbs.push({ link: '', label: this.tag.name });
    }
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.tracker.trackEventData(data);
  }
}
